import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import SlideshowImage from 'modules/SlideshowImage';
import moment from 'moment-timezone';
import { plato_api_question_stimuli_html_question_path } from '@/modules/routes';
import { IQuestion, IQuestionAnswer, IStudent, IAssignment } from '@/components/staffer/Grading/types';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import VideoPlayer from '@/components/common/VideoPlayer';
import useGradingStore, {
  QuestionsOptions,
  ShowHideOptions,
  StatusOptions,
} from '@/components/staffer/Grading/useGradingStore';

export const isAnswered = (questionAnswer?: IQuestionAnswer | null) => (
  !!questionAnswer && (questionAnswer.answer != null || Object.keys(questionAnswer.answer_data || {}).length > 0)
);

export interface StimuliData {
  gif?: string;
  image?: string;
  video?: unknown;
}

export const StimuliContent = ({ stimuli }: { stimuli?: StimuliData }) => {
  const stimuliContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stimuliContainerRef.current) return;
    const imgElements = stimuliContainerRef.current.querySelectorAll('img[data-zoomurl]');
    imgElements.forEach((img) => {
      if (img.getAttribute('init') === 'true') return;
      const slideshowImage = new SlideshowImage();
      slideshowImage.init(img as HTMLImageElement);
    });
  }, [stimuli?.image, stimuli?.gif]);

  if (!stimuli) return null;
  const { gif, image, video } = stimuli;

  if (!gif && !image && !video) return null;

  return (
    <div ref={stimuliContainerRef} className={styles.questionStimuli}>
      {image && <div dangerouslySetInnerHTML={{ __html: image }} />}
      {gif && <div dangerouslySetInnerHTML={{ __html: gif }} />}
      {video && (
        <div className={styles.questionVideo}>
          <VideoPlayer maxWidth={285} video={video} />
        </div>
      )}
    </div>
  );
};

export const renderStimuli = (question : IQuestion) => {
  const questionStimuliQuery = useQuery<StimuliData>(['questionStimuli', question.id], () => (
    Axios.get(plato_api_question_stimuli_html_question_path(question.id))
      .then(res => res.data)
  ));

  if (questionStimuliQuery.isLoading) return null;

  return <StimuliContent stimuli={questionStimuliQuery.data} />;
};

export const noAnswerProvided = () => (
  <div>
    <span className="student-response unanswered">No answer provided.</span>
  </div>
);

export const renderCorrectnessIcon = (isCorrect : boolean, className: string = '') => {
  const iconClass = isCorrect ? 'check-circle' : 'times-circle';
  return <i aria-hidden className={`fa fa-${iconClass} tw-mr-2 ${className}`} />;
};

interface ApplyGradeByStudentFilterProps {
  autoGradedFilter: ShowHideOptions;
  questionAnswers: IQuestionAnswer[];
  questions: IQuestion[];
  questionsFilter: QuestionsOptions;
  statusFilter: StatusOptions;
}

export const applyGradeByStudentFilters = ({
  autoGradedFilter,
  questionAnswers,
  questions,
  questionsFilter,
  statusFilter,
}: ApplyGradeByStudentFilterProps) => {
  let filteredQuestions: IQuestion[] = questions;

  const questionAnswerByQuestionId = questions.reduce((acc, question) => {
    acc[question.id] = questionAnswers?.find(qa => qa.question_id.toString() === question.id.toString());
    return acc;
  }, {});

  // Filter by answered/unanswered
  if (questionsFilter === 'answered') {
    filteredQuestions = filteredQuestions.filter(q => isAnswered(questionAnswerByQuestionId[q.id]));
  } else if (questionsFilter === 'unanswered') {
    filteredQuestions = filteredQuestions.filter(q => !isAnswered(questionAnswerByQuestionId[q.id]));
  }

  // Filter by graded/ungraded
  if (statusFilter === 'graded') {
    filteredQuestions = filteredQuestions.filter(q => questionAnswerByQuestionId[q.id]?.score != null);
  } else if (statusFilter === 'ungraded') {
    filteredQuestions = filteredQuestions.filter(q => questionAnswerByQuestionId[q.id]?.score == null);
  }

  // Filter by auto-gradable
  if (autoGradedFilter === 'hide') {
    filteredQuestions = filteredQuestions.filter(q => !q.auto_gradable);
  }

  return filteredQuestions;
};

interface ApplyGradeByQuestionFilterProps {
  questionAnswers: IQuestionAnswer[];
  questionsFilter: QuestionsOptions;
  statusFilter: StatusOptions;
  students: IStudent[];
}

export const applyGradeByQuestionFilters = ({
  questionAnswers,
  questionsFilter,
  statusFilter,
  students,
}: ApplyGradeByQuestionFilterProps) => {
  let filteredStudents: IStudent[] = students;

  const questionAnswerByStudentId = students.reduce((acc, student) => {
    acc[student.id] = questionAnswers?.find(qa => qa.student_id === student.id);
    return acc;
  }, {});

  // Filter by answered/unanswered
  if (questionsFilter === 'answered') {
    filteredStudents = filteredStudents.filter(s => isAnswered(questionAnswerByStudentId[s.id]));
  } else if (questionsFilter === 'unanswered') {
    filteredStudents = filteredStudents.filter(s => !isAnswered(questionAnswerByStudentId[s.id]));
  }

  // Filter by graded/ungraded
  if (statusFilter === 'graded') {
    filteredStudents = filteredStudents.filter(s => questionAnswerByStudentId[s.id]?.score != null);
  } else if (statusFilter === 'ungraded') {
    filteredStudents = filteredStudents.filter(s => questionAnswerByStudentId[s.id]?.score == null);
  }

  return filteredStudents;
};

// Use `exercise_type` instead of `type` on assignment_details, since `type` gets translated.
export const checkIfAssignmentIsSlideShow = (assignment: IAssignment) => (
  ['SlideGroup', 'SlideShow'].includes(assignment.assignment_details?.exercise_type)
);

// Notebook questions in a section, aka Reading Notes, can't be graded unless the assignment uses
// question scoring mode.
export const showsScoreInput = (assignment: IAssignment, question: IQuestion) => (
  checkIfAssignmentIsSlideShow(assignment)
  || question.auto_gradable
  || question.question_group.exercise_type !== 'Notebook'
  || assignment.scoring_mode === 'question'
);

export const formattedUpdatedAt = (updatedAt) => {
  if (updatedAt == null) return '--';

  const datetime = moment(updatedAt).format('MMM DD, hh:mm A');
  const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

  return `${datetime} ${timezone}`;
};

const navigationLessonOrUnitId = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const lessonId = searchParams.get('lesson_id');
  const unitId = searchParams.get('unit_id');

  return lessonId != null ? { lesson_id: lessonId } : { unit_id: unitId };
};

export const gradingNavigationParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const classroomId = searchParams.get('classroom_id');

  return {
    classroom_id: classroomId,
    ...navigationLessonOrUnitId(),
  };
};

export const gradingNavigationStringParams = (classroomId = null) => {
  const searchParams = new URLSearchParams(window.location.search);
  const classroomIdParam = searchParams.get('classroom_id');

  const params = {
    classroom_id: classroomId || classroomIdParam,
    ...navigationLessonOrUnitId(),
  };

  return Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
};

export const canViewCorrectAnswers = () => {
  const { assignment, isTeacher } = useGradingStore();
  const { show_answers_to_students: showAnswersToStudents } = assignment || {};

  return isTeacher || showAnswersToStudents;
};

export const roundedToDecimal = (num: number, power: number = 1): number => {
  const factor = 10 ** power;
  return Math.round(num * factor) / factor;
};

export const normalizedScore = (assignment: Pick<IAssignment, 'normalization_factor'>, score: number): number => {
  const normalizationFactor = roundedToDecimal(assignment.normalization_factor || 1, 2);

  return parseFloat(((score || 0) * normalizationFactor).toFixed(1));
};

export const denormalizedScore = (assignment: Pick<IAssignment, 'normalization_factor'>, score: number): number => (
  parseFloat(((score || 0) / (assignment.normalization_factor || 1)).toFixed(1))
);
